import type { ReactNode } from 'react'

import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import { DocumentDtoDocTypeEnum } from '@b-stock/listing-api-client'

import { AccordionCard } from '@/buyer-portal/components/layouts/CardLayout/card/AccordionCard'
import {
  CardGrid,
  type CardGridItem,
} from '@/buyer-portal/components/layouts/CardLayout/card/CardGrid'

import { DocumentLink } from '../DocumentLink'

const PolicyText = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.black};
  margin: 0;
`

export const ListingPoliciesCard = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) => {
  const personId = useDecodedAccessToken()?.personId
  const { documents } = listing
  const cancellationPolicy = listing.contract?.cancellationPolicy

  if (!personId || !documents) return null

  const items: CardGridItem[] = [
    {
      id: 'termsOfPurchase',
      isFullWidth: true,
      label: <FormattedMessage id="Auction.title.termsOfPurchase" />,
      value: (
        <PolicyText>
          <FormattedMessage
            id="Auction.body.policies"
            values={{
              link: (content: ReactNode) => (
                <DocumentLink
                  documents={documents}
                  documentType={DocumentDtoDocTypeEnum.TermsOfService}
                >
                  {content}
                </DocumentLink>
              ),
            }}
          />
        </PolicyText>
      ),
    },
  ]

  if (cancellationPolicy) {
    items.push({
      id: 'cancellationPolicy',
      isFullWidth: true,
      label: <FormattedMessage id="Auction.title.cancellationPolicy" />,
      value: <PolicyText>{cancellationPolicy}</PolicyText>,
    })
  }

  return (
    <AccordionCard title={<FormattedMessage id="Auction.title.policies" />}>
      <CardGrid items={items} />
    </AccordionCard>
  )
}
